/**
 *  @class
 *  @function KitBuilder
 *
 *  Step by step kit configurator. Each step is one piece of the kit; the
 *  customer picks colour/size per piece and the whole kit is added to the cart
 *  in a single request.
 *
 *  Cart modes:
 *   - bundle        native Shopify bundle. The kit product's variant that
 *                   matches the chosen colour/size of every piece is resolved
 *                   through sections/kit-bundle-variant.liquid
 *                   (?option_values=) and added to the cart. Price comes from
 *                   that variant, stock and the order lines come from the
 *                   pieces (Shopify expands the bundle into its components).
 *   - kit_product   the kit product itself is added (1 line item, kit price),
 *                   the chosen pieces travel as line item properties.
 *   - separate      each chosen variant is added as its own line item, so the
 *                   real stock of every piece is decremented.
 */
if (!customElements.get('kit-builder')) {
  class KitBuilder extends HTMLElement {
    connectedCallback() {
      this.sectionId = this.dataset.section;
      this.mode = this.dataset.mode || 'kit_product';
      this.kitVariantId = this.dataset.kitVariantId;
      this.kitAvailable = this.dataset.kitAvailable !== 'false';
      this.kitTitle = this.dataset.kitTitle || '';
      this.kitPrice = parseInt(this.dataset.kitPrice, 10) || 0;
      this.kitCompare = parseInt(this.dataset.kitCompare, 10) || 0;

      this.strings = {
        property: this.dataset.propertyLabel || 'Peça',
        unavailable: this.dataset.unavailableText || '',
        incomplete: this.dataset.incompleteText || '',
        soldOut: this.dataset.soldoutText || '',
        add: this.dataset.addText || '',
        savings: this.dataset.savingsText || ''
      };

      this.errorWrapper = this.querySelector('.kit-builder__error');
      this.errorText = this.querySelector('[data-kit-error-text]');
      this.submitButton = this.querySelector('[data-kit-submit]');
      this.submitText = this.querySelector('.kit-builder__submit-text');
      this.prevButton = this.querySelector('[data-kit-prev]');
      this.nextButton = this.querySelector('[data-kit-next]');
      this.summaryItems = Array.from(this.querySelectorAll('[data-kit-summary-item]'));
      this.totalDisplay = this.querySelector('[data-kit-total]');
      this.compareDisplay = this.querySelector('[data-kit-price-display] del .amount');
      this.savingsWrapper = this.querySelector('[data-kit-savings]');
      this.savingsAmount = this.querySelector('[data-kit-savings-amount]');
      this.paymentWrapper = this.querySelector('[data-kit-payment]');

      this.bundle = null;
      this.bundleVariant = null;
      this.bundlePending = false;
      this.bundleCache = new Map();
      this.bundleRequest = 0;
      if (this.mode === 'bundle') {
        try {
          const data = this.querySelector('[data-kit-bundle]');
          this.bundle = data ? JSON.parse(data.textContent) : null;
        } catch (e) {
          console.error('Kit builder: invalid bundle data', e);
        }
      }

      this.pieces = Array.from(this.querySelectorAll('[data-kit-step]')).map((el) => {
        let variants = [];
        try {
          variants = JSON.parse(el.querySelector('[data-kit-variants]').textContent);
        } catch (e) {
          console.error('Kit builder: invalid variant data', e);
        }
        return {
          el: el,
          variants: variants,
          fieldsets: Array.from(el.querySelectorAll('.kit-option')),
          thumb: el.querySelector('[data-kit-thumb]'),
          notice: el.querySelector('[data-kit-step-notice]'),
          title: el.dataset.productTitle || '',
          variant: null
        };
      });

      if (!this.pieces.length) return;

      this.currentStep = 0;

      this.addEventListener('change', this.onOptionChange.bind(this));
      this.prevButton?.addEventListener('click', () => this.goToStep(this.currentStep - 1));
      this.nextButton?.addEventListener('click', () => this.goToStep(this.currentStep + 1));
      this.querySelectorAll('[data-kit-goto]').forEach((button) => {
        button.addEventListener('click', () => this.goToStep(parseInt(button.dataset.kitGoto, 10)));
      });
      this.submitButton?.addEventListener('click', this.onSubmit.bind(this));

      this.pieces.forEach((piece) => this.refreshPiece(piece));
      this.updateNav();
      this.updateTotals();
      this.updateSubmitState();
      if (this.mode === 'bundle') this.resolveBundle();
    }

    /* Native bundle ----------------------------------------------------------- */

    pieceValue(piece, handle) {
      if (!piece.variant) return null;
      const index = piece.fieldsets.findIndex((fieldset) => fieldset.dataset.handle === handle);
      if (index < 0) return null;
      return piece.variant.options[index];
    }

    /**
     * Current selection expressed in the kit product's terms:
     * colours of piece 1 and 2 and the "size piece 1|size piece 2" key, plus
     * the matching option value IDs (null when a value has no kit option).
     */
    bundleSelection() {
      if (!this.bundle || this.pieces.length !== 2) return null;
      const colours = this.pieces.map((piece) => this.pieceValue(piece, 'cor'));
      const sizeKey = this.pieces.map((piece) => this.pieceValue(piece, 'tamanho')).join('|');
      const ids = [
        colours[0] !== null ? this.bundle.colors[0][colours[0]] : undefined,
        colours[1] !== null ? this.bundle.colors[1][colours[1]] : undefined,
        this.bundle.sizes[sizeKey]
      ];
      if (ids.some((id) => !id)) return null;
      return { colours: colours, sizeKey: sizeKey, ids: ids };
    }

    resolveBundle() {
      const selection = this.bundleSelection();
      if (!selection) {
        this.bundleRequest++;
        this.applyBundleVariant(null);
        return;
      }

      const key = selection.ids.join(',');
      if (this.bundleCache.has(key)) {
        this.bundleRequest++;
        this.applyBundleVariant(this.bundleCache.get(key));
        return;
      }

      const request = ++this.bundleRequest;
      this.bundlePending = true;
      this.updateSubmitState();

      const url = `${this.bundle.url}?sections=kit-bundle-variant&option_values=${key}`;
      // No "Accept: application/json" header here: Shopify would answer with
      // the product JSON instead of the rendered section.
      fetch(url)
        .then((response) => response.json())
        .then((sections) => {
          const html = sections['kit-bundle-variant'];
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const variant = JSON.parse(doc.querySelector('[data-kit-bundle-variant]').textContent);
          // option_values falls back to another variant on a partial match:
          // only accept the exact combination that was asked for.
          const sizes = String((variant.options && variant.options[2]) || '')
            .split(' · ')
            .map((part) => part.trim().split(' ').pop())
            .join('|');
          const exact =
            !!variant.id &&
            variant.options[0] === selection.colours[0] &&
            variant.options[1] === selection.colours[1] &&
            sizes === selection.sizeKey;
          const resolved = exact ? variant : null;
          if (resolved) this.bundleCache.set(key, resolved);
          if (request === this.bundleRequest) this.applyBundleVariant(resolved);
        })
        .catch((e) => {
          console.error('Kit builder: bundle variant lookup failed', e);
          if (request === this.bundleRequest) this.applyBundleVariant(null);
        });
    }

    applyBundleVariant(variant) {
      this.bundlePending = false;
      this.bundleVariant = variant;
      this.updateBundlePrice();
      this.updateSubmitState();
    }

    updateBundlePrice() {
      const variant = this.bundleVariant;
      if (!variant) {
        if (this.paymentWrapper) this.paymentWrapper.innerHTML = '';
        if (this.compareDisplay) this.compareDisplay.closest('del').hidden = true;
        if (this.savingsWrapper) this.savingsWrapper.classList.add('hidden');
        return;
      }

      let piecesTotal = 0;
      this.pieces.forEach((piece) => {
        if (piece.variant) piecesTotal += piece.variant.compare_at_price || piece.variant.price;
      });
      const price = variant.price;
      const compare = Math.max(variant.compare_at_price || 0, piecesTotal);

      if (this.totalDisplay) {
        this.totalDisplay.textContent = this.formatMoney(price);
        this.totalDisplay.classList.toggle('discounted', compare > price);
      }
      if (this.compareDisplay) {
        this.compareDisplay.textContent = this.formatMoney(compare);
        this.compareDisplay.closest('del').hidden = compare <= price;
      }
      if (this.savingsWrapper) {
        this.savingsWrapper.classList.toggle('hidden', compare <= price);
        if (this.savingsAmount) this.savingsAmount.textContent = this.formatMoney(compare - price);
      }
      if (this.paymentWrapper && typeof variant.payment_html === 'string') {
        this.paymentWrapper.innerHTML = variant.payment_html;
      }
    }

    /* Selection ------------------------------------------------------------ */

    getSelections(piece) {
      return piece.fieldsets.map((fieldset) => {
        const checked = fieldset.querySelector('input[type="radio"]:checked');
        if (checked) return checked.dataset.value;
        const select = fieldset.querySelector('select');
        return select ? select.value : null;
      });
    }

    setSelection(piece, index, value) {
      const fieldset = piece.fieldsets[index];
      if (!fieldset) return;
      const radio = Array.from(fieldset.querySelectorAll('input[type="radio"]')).find(
        (input) => input.dataset.value === value
      );
      if (radio) {
        radio.checked = true;
        return;
      }
      const select = fieldset.querySelector('select');
      if (select) select.value = value;
    }

    matchesPrefix(variant, selections, upTo) {
      for (let i = 0; i < upTo; i++) {
        if (selections[i] !== null && variant.options[i] !== selections[i]) return false;
      }
      return true;
    }

    resolveVariant(piece) {
      const selections = this.getSelections(piece);
      if (!piece.fieldsets.length) return piece.variants[0] || null;

      return (
        piece.variants.find((variant) =>
          selections.every((value, index) => variant.options[index] === value)
        ) || null
      );
    }

    /**
     * After a change on `changedIndex`, walk the following options and move any
     * that became impossible onto the first value that still works.
     */
    repairSelections(piece, changedIndex) {
      for (let i = changedIndex + 1; i < piece.fieldsets.length; i++) {
        const selections = this.getSelections(piece);
        const stillValid = piece.variants.some(
          (variant) =>
            variant.available &&
            variant.options[i] === selections[i] &&
            this.matchesPrefix(variant, selections, i)
        );
        if (stillValid) continue;

        const fallback =
          piece.variants.find(
            (variant) => variant.available && this.matchesPrefix(variant, selections, i)
          ) || piece.variants.find((variant) => this.matchesPrefix(variant, selections, i));

        if (fallback) this.setSelection(piece, i, fallback.options[i]);
      }
    }

    updateAvailability(piece) {
      const selections = this.getSelections(piece);

      piece.fieldsets.forEach((fieldset, index) => {
        const isSellable = (value) =>
          piece.variants.some(
            (variant) =>
              variant.available &&
              variant.options[index] === value &&
              this.matchesPrefix(variant, selections, index)
          );

        fieldset.querySelectorAll('input[type="radio"]').forEach((input) => {
          input.classList.toggle('is-disabled', !isSellable(input.dataset.value));
        });
        fieldset.querySelectorAll('select option').forEach((option) => {
          option.disabled = !isSellable(option.value);
        });

        const label = fieldset.querySelector('.form__label__value');
        if (label) label.textContent = selections[index] || '';
      });
    }

    refreshPiece(piece) {
      this.updateAvailability(piece);
      piece.variant = this.resolveVariant(piece);

      if (piece.thumb && piece.variant && piece.variant.image) {
        const image = piece.thumb.querySelector('img');
        if (image && image.getAttribute('src') !== piece.variant.image) {
          image.setAttribute('src', piece.variant.image);
          image.removeAttribute('srcset');
        }
      }

      this.updateGallery(piece);

      if (piece.notice) {
        const unavailable = !piece.variant || !piece.variant.available;
        piece.notice.hidden = !unavailable || !this.strings.unavailable;
        if (unavailable) piece.notice.textContent = this.strings.unavailable;
      }

      this.updateSummary();
    }

    updateGallery(piece) {
      const gallery = document.querySelector('[data-kit-gallery]');
      if (!gallery || !piece.variant || !Array.isArray(piece.variant.gallery) || !piece.variant.gallery.length) return;
      const index = this.pieces.indexOf(piece);
      const container = gallery.querySelector(`[data-kit-gallery-piece="${index}"]`);
      if (!container) return;
      const zoom = container.querySelector('[data-kit-gallery-zoom]');
      const first = piece.variant.gallery[0];
      if (zoom && first && zoom.getAttribute('src') !== first.replace(/width=\d+/, 'width=1800')) {
        zoom.setAttribute('src', first.replace(/width=\d+/, 'width=1800'));
        zoom.setAttribute(
          'srcset',
          [1000, 1400, 1800, 2400].map((width) => `${first.replace(/width=\d+/, `width=${width}`)} ${width}w`).join(', ')
        );
      }
      const images = container.querySelectorAll('[data-kit-gallery-img]');
      images.forEach((image, i) => {
        const url = piece.variant.gallery[i];
        image.hidden = !url;
        if (!url || image.getAttribute('src') === url) return;
        image.setAttribute('src', url);
        image.setAttribute(
          'srcset',
          [600, 1000, 1400, 1800].map((width) => `${url.replace(/width=\d+/, `width=${width}`)} ${width}w`).join(', ')
        );
      });
    }

    onOptionChange(event) {
      const fieldset = event.target.closest('.kit-option');
      if (!fieldset) return;

      const piece = this.pieces.find((item) => item.el.contains(fieldset));
      if (!piece) return;

      const changedIndex = piece.fieldsets.indexOf(fieldset);
      this.repairSelections(piece, changedIndex);
      this.refreshPiece(piece);
      this.updateTotals();
      this.updateSubmitState();
      if (this.mode === 'bundle') this.resolveBundle();
    }

    /* Steps ---------------------------------------------------------------- */

    goToStep(index) {
      if (index < 0 || index >= this.pieces.length) return;

      this.pieces.forEach((piece, i) => {
        piece.el.hidden = i !== index;
        piece.el.classList.toggle('kit-step--active', i === index);
      });
      this.currentStep = index;
      this.updateNav();
      this.updateSummary();
      // The gallery follows the step: only the current piece's photos show.
      document.querySelectorAll('[data-kit-gallery-piece]').forEach((figure) => {
        figure.hidden = figure.dataset.kitGalleryPiece !== String(index);
      });

      const card = this.querySelector('.kit-builder__card') || this;
      const top = card.getBoundingClientRect().top + window.scrollY - 120;
      if (window.scrollY > top) {
        window.scrollTo({ top: top, behavior: 'smooth' });
      }
      // The button that was clicked may now be hidden: keep focus in the card.
      this.pieces[index].el.querySelector('.kit-step__bar')?.focus({ preventScroll: true });
    }

    updateNav() {
      const last = this.currentStep >= this.pieces.length - 1;
      if (this.prevButton) this.prevButton.disabled = this.currentStep === 0;
      // One button at a time: "next piece" until the last step, then "add kit".
      if (this.nextButton) {
        this.nextButton.hidden = last;
        if (this.submitButton) this.submitButton.hidden = !last;
      }
    }

    updateSummary() {
      this.summaryItems.forEach((item, index) => {
        const piece = this.pieces[index];
        if (!piece) return;
        const value = item.querySelector('[data-kit-summary-value]');
        const complete = !!(piece.variant && piece.variant.available);

        item.classList.toggle('kit-builder__summary-item--complete', complete);
        item.classList.toggle('kit-builder__summary-item--active', index === this.currentStep);

        if (value) {
          value.textContent =
            piece.variant && piece.variant.title !== 'Default Title' ? piece.variant.title : '';
        }
      });
    }

    /* Pricing -------------------------------------------------------------- */

    updateTotals() {
      if (this.mode !== 'separate') return;
      // A half resolved selection would show a total lower than what the
      // customer will actually pay, so keep the last good figure instead.
      if (this.pieces.some((piece) => !piece.variant)) return;

      let total = 0;
      let compare = 0;
      this.pieces.forEach((piece) => {
        total += piece.variant.price;
        compare += piece.variant.compare_at_price || piece.variant.price;
      });

      if (this.totalDisplay) this.totalDisplay.textContent = this.formatMoney(total);
      if (this.compareDisplay) {
        this.compareDisplay.textContent = this.formatMoney(compare);
        this.compareDisplay.closest('del').hidden = compare <= total;
      }
      if (this.savingsWrapper) {
        this.savingsWrapper.classList.toggle('hidden', compare <= total);
        if (this.savingsAmount) this.savingsAmount.textContent = this.formatMoney(compare - total);
      }
    }

    formatMoney(cents) {
      const format = (window.theme && theme.settings && theme.settings.money_with_currency_format) || '${{amount}}';
      const value = typeof cents === 'string' ? cents.replace('.', '') : cents;

      const withDelimiters = (number, precision = 2, thousands = ',', decimal = '.') => {
        if (isNaN(number) || number === null) return 0;
        const fixed = (number / 100.0).toFixed(precision);
        const parts = fixed.split('.');
        const dollars = parts[0].replace(/(\d)(?=(\d\d\d)+(?!\d))/g, `$1${thousands}`);
        const cents_part = parts[1] ? decimal + parts[1] : '';
        return dollars + cents_part;
      };

      const placeholder = /\{\{\s*(\w+)\s*\}\}/;
      const match = format.match(placeholder);
      if (!match) return format;

      let formatted;
      switch (match[1]) {
        case 'amount':
          formatted = withDelimiters(value, 2);
          break;
        case 'amount_no_decimals':
          formatted = withDelimiters(value, 0);
          break;
        case 'amount_with_comma_separator':
          formatted = withDelimiters(value, 2, '.', ',');
          break;
        case 'amount_with_space_separator':
          formatted = withDelimiters(value, 2, ' ', ',');
          break;
        case 'amount_with_apostrophe_separator':
          formatted = withDelimiters(value, 2, "'", '.');
          break;
        case 'amount_no_decimals_with_comma_separator':
          formatted = withDelimiters(value, 0, '.', ',');
          break;
        case 'amount_no_decimals_with_space_separator':
          formatted = withDelimiters(value, 0, ' ');
          break;
        case 'amount_with_period_and_space_separator':
          formatted = withDelimiters(value, 2, ' ', '.');
          break;
        default:
          formatted = withDelimiters(value, 2);
      }

      return format.replace(placeholder, formatted);
    }

    /* Add to cart ---------------------------------------------------------- */

    isComplete() {
      if (this.mode === 'kit_product' && (!this.kitVariantId || !this.kitAvailable)) return false;
      const piecesReady = this.pieces.every((piece) => piece.variant && piece.variant.available);
      if (this.mode === 'bundle') {
        return piecesReady && !this.bundlePending && !!this.bundleVariant && this.bundleVariant.available;
      }
      return piecesReady;
    }

    updateSubmitState() {
      if (!this.submitButton) return;

      const complete = this.isComplete();
      this.submitButton.disabled = !complete;
      this.submitButton.classList.toggle('kit-builder__submit--pending', this.mode === 'bundle' && this.bundlePending);

      if (!this.submitText) return;
      // Every piece resolved to a variant, but the chosen combination can't be
      // sold (piece out of stock, kit variant missing/unavailable).
      const allChosen = this.pieces.every((piece) => piece.variant);
      const soldOut =
        allChosen &&
        (this.pieces.some((piece) => !piece.variant.available) ||
          (this.mode === 'bundle' && !this.bundlePending && (!this.bundleVariant || !this.bundleVariant.available)) ||
          (this.mode === 'kit_product' && !this.kitAvailable));

      if (complete || (this.mode === 'bundle' && this.bundlePending)) {
        this.submitText.textContent = this.strings.add;
      } else if (soldOut && this.strings.soldOut) {
        this.submitText.textContent = this.strings.soldOut;
      } else if (this.strings.incomplete) {
        this.submitText.textContent = this.strings.incomplete;
      } else {
        this.submitText.textContent = this.strings.add;
      }
    }

    buildItems() {
      if (this.mode === 'bundle') {
        if (!this.bundleVariant || !this.bundleVariant.id) return [];
        return [{ id: this.bundleVariant.id, quantity: 1 }];
      }

      if (this.mode === 'separate') {
        const reference = `${this.kitTitle} #${Date.now().toString(36)}`;
        return this.pieces.map((piece) => ({
          id: piece.variant.id,
          quantity: 1,
          properties: {
            Kit: this.kitTitle,
            _kit_ref: reference
          }
        }));
      }

      const properties = {};
      this.pieces.forEach((piece, index) => {
        const key = `${this.strings.property} ${index + 1}`;
        const detail =
          piece.variant.title && piece.variant.title !== 'Default Title'
            ? `${piece.title} — ${piece.variant.title}`
            : piece.title;
        properties[key] = detail;
        properties[`_kit_variant_${index + 1}`] = String(piece.variant.id);
      });

      return [
        {
          id: this.kitVariantId,
          quantity: 1,
          properties: properties
        }
      ];
    }

    onSubmit() {
      if (!this.isComplete() || this.submitButton.classList.contains('loading')) return;

      this.showError(false);
      this.submitButton.classList.add('loading');
      this.submitButton.setAttribute('aria-disabled', true);

      fetch(theme.routes.cart_add_url, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json'
        },
        body: JSON.stringify({
          items: this.buildItems(),
          sections: this.getSectionsToRender().map((section) => section.section),
          sections_url: window.location.pathname
        })
      })
        .then((response) => response.json().then((data) => ({ ok: response.ok, httpStatus: response.status, data: data })))
        .then(({ ok, httpStatus, data }) => {
          const response = data || {};
          // Errors come either as {status, description} or as {errors: "..."}.
          if (!ok || response.status || response.errors) {
            const errors = response.errors;
            const message =
              response.description ||
              (typeof errors === 'string' ? errors : errors && Object.values(errors).flat().join(' ')) ||
              response.message ||
              this.strings.unavailable;
            this.showError(message);
            if (this.mode === 'bundle' && this.bundleVariant && httpStatus === 422) {
              const failed = this.bundleVariant;
              this.bundleCache.forEach((value, cacheKey) => {
                if (value === failed) this.bundleCache.delete(cacheKey);
              });
              this.bundleVariant = Object.assign({}, failed, { available: false });
              this.updateSubmitState();
            }
            if (httpStatus === 422 || response.status === 422) {
              document.documentElement.dispatchEvent(new CustomEvent('cart:refresh', { bubbles: true }));
            }
            return;
          }

          this.renderContents(response);
          dispatchCustomEvent('cart:item-added', {
            product: response.items ? response.items[0] : response
          });
          this.openCartDrawer();
        })
        .catch((e) => {
          console.error(e);
          this.showError(this.strings.unavailable);
        })
        .finally(() => {
          this.submitButton.classList.remove('loading');
          this.submitButton.removeAttribute('aria-disabled');
        });
    }

    showError(message) {
      if (!this.errorWrapper) return;
      this.errorWrapper.hidden = !message;
      if (message && this.errorText) this.errorText.textContent = message;
    }

    getSectionsToRender() {
      return [
        { id: 'Cart', section: 'main-cart', selector: '.thb-cart-form' },
        { id: 'Cart-Drawer', section: 'cart-drawer', selector: '.cart-drawer' },
        { id: 'cart-drawer-toggle', section: 'cart-bubble', selector: '.thb-item-count' }
      ];
    }

    renderContents(parsedState) {
      if (!parsedState.sections) return;

      this.getSectionsToRender().forEach((section) => {
        const container = document.getElementById(section.id);
        if (!container || !parsedState.sections[section.section]) return;

        const target = container.querySelector(section.selector) || container;
        target.innerHTML = this.getSectionInnerHTML(parsedState.sections[section.section], section.selector);

        if (section.id === 'Cart-Drawer') {
          container.notesToggle?.();
          container.removeProductEvent?.();
        }
        if (section.id === 'Cart' && typeof Cart !== 'undefined') {
          new Cart().renderContents(parsedState);
        }
      });
    }

    getSectionInnerHTML(html, selector = '.shopify-section') {
      const parsed = new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
      return parsed ? parsed.innerHTML : '';
    }

    openCartDrawer() {
      const drawer = document.getElementById('Cart-Drawer');
      if (!drawer) {
        window.location.href = theme.routes.cart_url;
        return;
      }
      document.body.classList.add('open-cc');
      document.body.classList.add('open-cart');
      drawer.classList.add('active');
      setTimeout(() => {
        drawer.querySelector('.product-recommendations--full')?.classList.add('active');
      });
      dispatchCustomEvent('cart-drawer:open');
    }
  }

  customElements.define('kit-builder', KitBuilder);
}
